import { getAnnualFinancialOutlook } from './dashboard.annual';
import { getDealObligations, isCancelledRecord, normalizedStatus } from './dashboard.obligations';
import { getPaymentAccountingDate, getExpenseAccountingDate } from '../../accounting/utils/accountingDates';

export function formatCurrency(amount) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

export function formatDateRangeLabel(range) {
  const format = (value) => parseDateOnly(value)?.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  if (!range?.from && !range?.to) return 'Da sempre';
  if (!range?.from) return `Fino al ${format(range.to)}`;
  if (!range?.to) return `Dal ${format(range.from)}`;
  return `${format(range.from)} - ${format(range.to)}`;
}

export function toLocalDateKey(value) {
  const date = parseDateOnly(value);
  return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
}

export function formatChartAmount(value) {
  return new Intl.NumberFormat('it-IT', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value) || 0);
}

export function buildDateRangeFromPreset(preset) {
  const today = new Date();
  let from = null;
  let to = today;

  switch (preset) {
    case 'oggi':
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      to = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      break;
    case 'settimana':
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
      break;
    case 'mese':
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'trimestre': {
      const quarter = Math.floor(today.getMonth() / 3);
      from = new Date(today.getFullYear(), quarter * 3, 1);
      break;
    }
    case 'anno':
      from = new Date(today.getFullYear(), 0, 1);
      break;
    case 'sempre':
    default:
      from = null;
      to = today;
  }

  return { from, to };
}

export function isDateInRange(dateStr, range) {
  if (!range?.from && !range?.to) return true;
  const date = parseDateOnly(dateStr);
  if (!date) return false;
  const from = parseDateOnly(range?.from);
  const to = parseDateOnly(range?.to);
  return (!from || date >= from) && (!to || date <= to);
}

function parseDateOnly(value) {
  if (!value) return null;
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function amountInCents(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

export function getBalanceTone(value) {
  const cents = amountInCents(value);
  if (cents > 0) return 'success';
  if (cents < 0) return 'attention';
  return 'neutral';
}

function sumAmounts(rows) {
  return rows.reduce((sum, row) => sum + amountInCents(row.amount), 0) / 100;
}

const EXCLUDED_ACCOUNTING_STATUSES = new Set([
  'stornato', 'stornata', 'annullato', 'annullata', 'cancellato', 'cancellata',
  'archiviato', 'archiviata', 'voided', 'reversed', 'cancelled', 'canceled',
]);
const isActiveMovement = (row) => !EXCLUDED_ACCOUNTING_STATUSES.has(normalizeStatus(row.status));

function getTodayDateOnly() {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

export function getUpcomingEventsCount(events) {
  const start = getTodayDateOnly();
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  end.setHours(23, 59, 59, 999);

  return events.filter((event) => {
    const hasDate = typeof event?.date === 'string' && event.date;
    if (!hasDate) return false;

    const eventDate = event?.time
      ? new Date(`${event.date}T${event.time}:00`)
      : new Date(`${event.date}T23:59:59`);

    if (Number.isNaN(eventDate.getTime())) {
      return false;
    }

    return eventDate >= start && eventDate <= end;
  }).length;
}

export function getStatsCards(data) {
  const activeAssignments = data.assignments.filter((a) => a.status === 'in_corso');
  const overdueItems = data.overdueItems || getOverdueItems(data);
  const calendarEventsUnavailable = data.calendarEventsUnavailable === true;

  return [
    {
      title: 'Task attive',
      value: activeAssignments.length,
      icon: 'ClipboardList',
      variant: 'assignments',
      routeId: 'assignments',
      search: '?scope=team&assignmentTab=in_corso',
    },
    {
      title: 'Affari Attivi',
      value: data.dealsInProgress.length,
      icon: 'TrendingUp',
      variant: 'deals',
      routeId: 'deals-in-progress',
    },
    {
      title: 'Eventi',
      value: calendarEventsUnavailable ? '—' : getUpcomingEventsCount(data.calendarEvents),
      subtitle: calendarEventsUnavailable ? 'dato non disponibile' : data.calendarEventsPartial ? 'prossimi 7 giorni · dati parziali' : 'prossimi 7 giorni',
      icon: 'Calendar',
      variant: 'events',
      routeId: 'calendar',
      search: '?range=next7days',
    },
    {
      title: 'In ritardo',
      value: overdueItems.length,
      ...(data.overdueUnavailableModules?.length ? { subtitle: 'Dati parziali' } : {}),
      icon: 'AlertTriangle',
      variant: 'urgent',
      routeId: 'dashboard',
      search: '?view=overdue',
    },
  ];
}

export function getFilteredPayments(payments, dateRange) {
  return payments.filter((p) => isActiveMovement(p) && isDateInRange(getPaymentAccountingDate(p), dateRange));
}

export function getFilteredExpenses(expenses, dateRange) {
  return expenses.filter((e) => isActiveMovement(e) && isDateInRange(getExpenseAccountingDate(e), dateRange));
}

export function getFilteredAssignments(assignments, dateRange) {
  return assignments.filter((assignment) => {
    const referenceDate = assignment.createdAt || assignment.dueDate;
    return isDateInRange(referenceDate, dateRange);
  });
}

export function getAccountingMetrics(payments, expenses, dateRange, sources = {}) {
  const filteredPayments = getFilteredPayments(payments, dateRange);
  const filteredExpenses = getFilteredExpenses(expenses, dateRange);

  const receivedPayments = filteredPayments.filter((p) => normalizeStatus(p.status) === 'incassato');
  const paidExpenses = filteredExpenses.filter((e) => normalizeStatus(e.status) === 'pagato');
  const totalIncassi = sumAmounts(filteredPayments);
  const incassati = sumAmounts(receivedPayments);
  const totalUscite = sumAmounts(filteredExpenses);
  const uscitePagate = sumAmounts(paidExpenses);
  const saldoCorrente = (amountInCents(incassati) - amountInCents(uscitePagate)) / 100;
  const utilePeriodo = saldoCorrente;
  const bounds = resolveLineChartBounds(filteredPayments, filteredExpenses, dateRange);
  const effectiveEnd = new Date(Math.min(bounds.to.getTime(), getTodayDateOnly().getTime()));
  const effectiveStart = bounds.from;
  const monthlyProfits = getLineChartData(
    receivedPayments, paidExpenses, { from: effectiveStart, to: effectiveEnd }, true
  ).map((bucket) => bucket.utile);
  const guadagnoMensile = monthlyProfits.at(-1) || 0;
  const mediaGuadagnoMensile = monthlyProfits.length
    ? Math.round(monthlyProfits.reduce((sum, value) => sum + amountInCents(value), 0) / monthlyProfits.length) / 100
    : 0;
  const proiezioneAnnua = getAnnualFinancialOutlook({ payments, expenses, ...sources }, dateRange);

  return {
    filteredPayments,
    filteredExpenses,
    receivedPayments,
    paidExpenses,
    totalIncassi,
    incassati,
    totalUscite,
    uscitePagate,
    saldoCorrente,
    utilePeriodo,
    guadagnoMensile,
    mediaGuadagnoMensile,
    proiezioneAnnua,
  };
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function differenceInDays(from, to) {
  const utcDay = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(1, Math.round((utcDay(to) - utcDay(from)) / 86400000) + 1);
}

function buildDailyBuckets(from, to) {
  const buckets = [];
  const current = startOfDay(from);
  const end = endOfDay(to);

  while (current <= end) {
    buckets.push({
      key: toLocalDateKey(current),
      mese: current.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      fatturato: 0,
      costi: 0,
      utile: 0,
    });
    current.setDate(current.getDate() + 1);
  }

  return buckets;
}

function buildMonthlyBuckets(from, to) {
  const buckets = [];
  const current = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);

  while (current <= end) {
    buckets.push({
      key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
      mese: current.toLocaleDateString('it-IT', { month: 'short', ...(from.getFullYear() !== to.getFullYear() ? { year: 'numeric' } : {}) }),
      fatturato: 0,
      costi: 0,
      utile: 0,
    });
    current.setMonth(current.getMonth() + 1);
  }

  return buckets;
}

function resolveLineChartBounds(payments, expenses, dateRange) {
  const dataDates = [...payments.map(getPaymentAccountingDate), ...expenses.map(getExpenseAccountingDate)]
    .map(parseDateOnly).filter(Boolean).sort((left, right) => left - right);
  const today = getTodayDateOnly();
  const to = parseDateOnly(dateRange?.to) || new Date(Math.max(today.getTime(), dataDates.at(-1)?.getTime() || 0));
  const firstDate = dataDates.find((date) => date <= to) || to;
  const from = parseDateOnly(dateRange?.from) || firstDate;
  return { from: startOfDay(from), to: endOfDay(to) };
}

export function getLineChartData(
  payments,
  expenses,
  dateRange = { from: null, to: new Date() },
  forceMonthly = false
) {
  const activePayments = getFilteredPayments(payments, dateRange);
  const activeExpenses = getFilteredExpenses(expenses, dateRange);
  const bounds = resolveLineChartBounds(activePayments, activeExpenses, dateRange);
  if (bounds.from > bounds.to) return [];
  const days = differenceInDays(bounds.from, bounds.to);
  const buckets =
    days <= 45 && !forceMonthly
      ? buildDailyBuckets(bounds.from, bounds.to)
      : buildMonthlyBuckets(bounds.from, bounds.to);
  const bucketsByKey = new Map(buckets.map((bucket) => [bucket.key, bucket]));

  activePayments.forEach((payment) => {
    const accountingDate = getPaymentAccountingDate(payment);
    const date = parseDateOnly(accountingDate);
    if (!(date instanceof Date) || Number.isNaN(date?.getTime?.())) {
      return;
    }
    const key =
      days <= 45 && !forceMonthly
        ? toLocalDateKey(date)
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const bucket = bucketsByKey.get(key);
    if (bucket) {
      bucket.fatturato += amountInCents(payment.amount);
    }
  });

  activeExpenses.forEach((expense) => {
    const accountingDate = getExpenseAccountingDate(expense);
    const date = parseDateOnly(accountingDate);
    if (!(date instanceof Date) || Number.isNaN(date?.getTime?.())) {
      return;
    }
    const key =
      days <= 45 && !forceMonthly
        ? toLocalDateKey(date)
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const bucket = bucketsByKey.get(key);
    if (bucket) {
      bucket.costi += amountInCents(expense.amount);
    }
  });

  buckets.forEach((bucket) => {
    bucket.utile = (bucket.fatturato - bucket.costi) / 100;
    bucket.fatturato /= 100;
    bucket.costi /= 100;
  });

  return buckets;
}

const CLOSED_ASSIGNMENT_STATUSES = new Set([
  'rifiutato', 'rifiutata', 'rejected', 'cancelled', 'canceled',
  'completato',
  'completata',
  'completed',
  'done',
  'chiuso',
  'chiusa',
  'annullato',
  'annullata',
  'cancellato',
  'cancellata',
  'archiviato',
  'archiviata',
]);

const PAID_OR_CLOSED_PAYMENT_STATUSES = new Set([
  'incassato',
  'pagato',
  'paid',
  'saldato',
  'chiuso',
  'annullato',
  'cancellato',
  'stornato',
  'voided',
  'reversed',
]);

const PAID_OR_CLOSED_EXPENSE_STATUSES = new Set([
  'pagato',
  'pagata',
  'paid',
  'saldato',
  'saldata',
  'registrato',
  'registrata',
  'chiuso',
  'chiusa',
  'annullato',
  'annullata',
  'cancellato',
  'cancellata',
  'archiviato',
  'archiviata',
]);

function isPastDue(dueDate) {
  const parsed = parseDateOnly(dueDate);
  return Boolean(parsed && parsed < getTodayDateOnly());
}

function normalizeStatus(status) {
  return String(status || '')
    .trim()
    .toLowerCase();
}

export function isOverdueAssignment(assignment = {}) {
  return isPastDue(assignment.dueDate) && !CLOSED_ASSIGNMENT_STATUSES.has(normalizeStatus(assignment.status));
}

export function isOverduePayment(payment = {}) {
  return !isCancelledRecord(payment) && isPastDue(payment.dueDate) && !PAID_OR_CLOSED_PAYMENT_STATUSES.has(normalizeStatus(payment.status));
}

export function isOverdueExpense(expense = {}) {
  return !isCancelledRecord(expense) && isPastDue(expense.dueDate) && !PAID_OR_CLOSED_EXPENSE_STATUSES.has(normalizeStatus(expense.status));
}

export function getOverdueItems({ assignments = [], payments = [], expenses = [], calendarEvents = [], ...deals } = {}) {
  return [
    ...assignments.filter(isOverdueAssignment).map((assignment) => ({
      id: `task:${assignment.id}`,
      type: 'task',
      typeLabel: 'Task', entityType: 'assignment', entityId: assignment.id,
      title: assignment.title || assignment.description || `Task ${assignment.id}`,
      dueDate: assignment.dueDate,
      status: assignment.status || '—',
      route: `/incarichi?assignmentId=${encodeURIComponent(String(assignment.id))}`,
      raw: assignment,
    })),
    ...payments.filter(isOverduePayment).map((payment) => ({
      id: `payment:${payment.id}`,
      type: 'payments',
      typeLabel: 'Incassi e fatture', entityType: 'payment', entityId: payment.id, amount: payment.amount, counterparty: payment.client || '', installment: payment.installment || '',
      title: payment.invoiceNumber || payment.assignment || payment.client || `Pagamento ${payment.id}`,
      dueDate: payment.dueDate,
      status: payment.status || '—',
      route: `/contabilita-aziendale?paymentId=${encodeURIComponent(String(payment.id))}`,
      raw: payment,
    })),
    ...expenses.filter(isOverdueExpense).map((expense) => ({
      id: `expense:${expense.id}`,
      type: 'expenses',
      typeLabel: 'Uscite', entityType: 'expense', entityId: expense.id, amount: expense.amount, counterparty: expense.supplierName || '',
      title: expense.description || expense.supplierName || `Spesa ${expense.id}`,
      dueDate: expense.dueDate,
      status: expense.status || '—',
      route: `/uscite?expenseId=${encodeURIComponent(String(expense.id))}`,
      raw: expense,
    })),
    ...getDealObligations({ payments, ...deals }).filter((item) => isPastDue(item.dueDate)),
    ...calendarEvents.filter((event) => !event.isReadOnly && event.source !== 'derived'
      && ['deadline', 'scadenza', 'training', 'reminder', 'promemoria', 'incasso', 'pagamento'].includes(normalizedStatus(event.type))
      && !isCancelledRecord(event) && !['completed', 'completato', 'done', 'pagato', 'incassato'].includes(normalizedStatus(event.status))
      && isPastDue(event.date || event.startsAt)).map((event) => ({
        id: `calendar:${event.id}`, type: 'calendar', typeLabel: 'Calendario', entityType: 'calendarEvent', entityId: event.id,
        title: event.title || 'Scadenza calendario', dueDate: event.date || String(event.startsAt).slice(0, 10),
        status: event.status || 'da_completare', route: `/calendario?eventId=${encodeURIComponent(event.id)}`, raw: event,
      })),
  ].sort((left, right) => String(left.dueDate || '').localeCompare(String(right.dueDate || '')));
}

export function addChartTotals(data) {
  const total = data.reduce((sum, d) => sum + amountInCents(d.value), 0) / 100;
  return data.map((d) => ({ ...d, total }));
}

export function getChartColor(colorKey) {
  const colorMap = {
    success: 'var(--chart-success)',
    greenStrong: 'var(--chart-green-strong)',
    greenSoft: 'var(--chart-green-soft)',
    greenMid: 'var(--chart-green-mid)',
    greenMuted: 'var(--chart-green-muted)',
    redStrong: 'var(--chart-red-strong)',
    redSoft: 'var(--chart-red-soft)',
    redMid: 'var(--chart-red-mid)',
    danger: 'var(--chart-danger)',
    info: 'var(--chart-info)',
    warning: 'var(--chart-warning)',
    accent: 'var(--primary)',
    purple: 'var(--chart-accent-1)',
    cyan: 'var(--chart-info)',
    orange: 'var(--chart-warning)',
    lime: 'var(--chart-success)',
    pink: 'var(--chart-danger)',
    teal: 'var(--chart-accent-2)',
  };

  return colorMap[colorKey] || 'var(--color-text-muted)';
}

export function getBilancioChartData(incassati, uscitePagate) {
  return addChartTotals([
    { name: 'Incassi', value: incassati || 0, colorKey: 'success' },
    { name: 'Uscite', value: uscitePagate || 0, colorKey: 'danger' },
  ]);
}

export function getIncassiClientiChartData(payments) {
  const grouped = {};

  payments.forEach((p) => {
    grouped[p.client || 'Altri'] = (grouped[p.client || 'Altri'] || 0) + amountInCents(p.amount);
  });

  const colorKeys = ['success', 'greenStrong', 'greenSoft', 'greenMid', 'greenMuted'];

  return addChartTotals(
    Object.entries(grouped).map(([name, value], index) => ({
      name,
      value: value / 100,
      colorKey: colorKeys[index % colorKeys.length],
    }))
  );
}

export function getSpeseTipoChartData(expenses) {
  const grouped = {};
  const expenseTypeLabels = {
    stipendio: 'Stipendi',
    gestione: 'Gestione',
    spesa_aziendale: 'Spese Aziendali',
    pagamento: 'Pagamenti Fornitori',
  };

  expenses.forEach((e) => {
    const tipo = expenseTypeLabels[e.type] || e.type || 'Tipo non specificato';

    grouped[tipo] = (grouped[tipo] || 0) + amountInCents(e.amount);
  });

  const colorKeys = ['danger', 'redStrong', 'redSoft', 'redMid'];

  return addChartTotals(
    Object.entries(grouped).map(([name, value], index) => ({
      name,
      value: value / 100,
      colorKey: colorKeys[index % colorKeys.length],
    }))
  );
}

export function getAssignmentStats(assignments) {
  const totale = assignments.length;
  const completati = assignments.filter((a) => a.status === 'completato').length;
  const inCorso = assignments.filter((a) => a.status === 'in_corso').length;
  const daAccettare = assignments.filter((a) => a.status === 'da_accettare').length;
  const inAttesa = assignments.filter((a) => a.status === 'in_attesa').length;
  const inRitardo = assignments.filter(isOverdueAssignment).length;
  const urgentiAlta = assignments.filter((a) => a.urgency === 'alta').length;
  const urgentiMedia = assignments.filter((a) => a.urgency === 'media').length;
  const urgentiBassa = assignments.filter((a) => a.urgency === 'bassa').length;

  return {
    totale,
    completati,
    inCorso,
    daAccettare,
    inAttesa,
    inRitardo,
    urgentiAlta,
    urgentiMedia,
    urgentiBassa,
    tassoCompletamento: totale > 0 ? Math.round((completati / totale) * 100) : 0,
  };
}

export function getAssignmentStatusChartData(stats) {
  return addChartTotals(
    [
      { name: 'Completati', value: stats.completati, colorKey: 'success' },
      { name: 'In Corso', value: stats.inCorso, colorKey: 'info' },
      { name: 'Da Accettare', value: stats.daAccettare, colorKey: 'warning' },
      { name: 'In Attesa', value: stats.inAttesa, colorKey: 'purple' },
    ].filter((d) => d.value > 0)
  );
}

export function getAssignmentSenderChartData(assignments) {
  const grouped = {};
  const colorKeys = ['cyan', 'purple', 'warning', 'pink', 'teal'];

  assignments.forEach((a) => {
    grouped[a.sender] = (grouped[a.sender] || 0) + 1;
  });

  return addChartTotals(
    Object.entries(grouped).map(([name, value], index) => ({
      name,
      value,
      colorKey: colorKeys[index % colorKeys.length],
    }))
  );
}

export function getAssignmentTypeChartData(assignments) {
  const perTipo = {
    gestione_affare: { name: 'Gestione Affare', value: 0, colorKey: 'accent' },
    gestione_cliente: { name: 'Gestione Cliente', value: 0, colorKey: 'cyan' },
    documentazione: { name: 'Documentazione', value: 0, colorKey: 'orange' },
    altro: { name: 'Altro', value: 0, colorKey: 'lime' },
  };

  assignments.forEach((a) => {
    if (perTipo[a.type]) perTipo[a.type].value += 1;
  });

  return addChartTotals(Object.values(perTipo).filter((d) => d.value > 0));
}

export function getAssignmentUrgencyChartData(stats) {
  return addChartTotals(
    [
      { name: 'Alta', value: stats.urgentiAlta, colorKey: 'danger' },
      { name: 'Media', value: stats.urgentiMedia, colorKey: 'warning' },
      { name: 'Bassa', value: stats.urgentiBassa, colorKey: 'info' },
    ].filter((d) => d.value > 0)
  );
}

export function getStaffPerformance(assignments, staff) {
  const performanceByStaffId = new Map();
  const staffIdAliases = new Map();

  staff.forEach((s) => {
    if (s?.id === null || s?.id === undefined || s?.id === '') return;

    const canonicalId = String(s.id);
    performanceByStaffId.set(canonicalId, { ricevuti: 0, inCorso: 0, completati: 0 });
    [s.id, s.staffId, s.userId, s.linkedWorkspaceUserId, s.workspaceUserId]
      .filter((value) => value !== null && value !== undefined && value !== '')
      .forEach((value) => {
        staffIdAliases.set(String(value), canonicalId);
      });
  });

  assignments.forEach((a) => {
    const recipientIds = [
      ...(Array.isArray(a.recipientIds) ? a.recipientIds : []),
      ...(Array.isArray(a.staffIds) ? a.staffIds : []),
      ...(Array.isArray(a.assigneeIds) ? a.assigneeIds : []),
      a.staffId,
      a.assigneeId,
      a.assignedTo,
      a.responsibleId,
      a.userId,
    ].filter((value) => value !== null && value !== undefined && value !== '');

    const canonicalRecipientIds = new Set(
      recipientIds.map((recipientId) => staffIdAliases.get(String(recipientId))).filter(Boolean)
    );

    canonicalRecipientIds.forEach((canonicalId) => {
      const performance = performanceByStaffId.get(canonicalId);
      if (!performance) return;

      performance.ricevuti += 1;

      if (a.status === 'completato') performance.completati += 1;
      if (a.status === 'in_corso') performance.inCorso += 1;
    });
  });

  return staff
    .filter((s) => s?.id !== null && s?.id !== undefined && s?.id !== '')
    .map((s) => {
      const name = String(s.name || s.displayName || s.fullName || '').trim();
      const fullName = String(s.fullName || s.displayName || s.name || '').trim();

      return {
        name,
        fullName,
        ricevuti: performanceByStaffId.get(String(s.id))?.ricevuti || 0,
        inCorso: performanceByStaffId.get(String(s.id))?.inCorso || 0,
        completati: performanceByStaffId.get(String(s.id))?.completati || 0,
      };
    })
    .filter((s) => s.name && s.ricevuti > 0);
}

export function getDealSummary({ dealProposals, dealsInProgress, dealsCompleted }) {
  const proposalsValue = dealProposals.reduce((sum, d) => sum + d.estimatedValue, 0);
  const inProgressValue = dealsInProgress.reduce((sum, d) => sum + d.value, 0);
  const completedValue = dealsCompleted.reduce((sum, d) => sum + d.value, 0);

  return {
    proposalsCount: dealProposals.length,
    inProgressCount: dealsInProgress.length,
    completedCount: dealsCompleted.length,
    proposalsValue,
    inProgressValue,
    completedValue,
    totalPipelineValue: proposalsValue + inProgressValue + completedValue,
  };
}

export function getClientsSummary({ clients, dealProposals, dealsInProgress, dealsCompleted }) {
  const activeClients = new Set();
  dealsInProgress.forEach((d) => activeClients.add(d.client));

  const proposalClients = new Set();
  const completedClients = new Set();

  dealProposals.forEach((d) => proposalClients.add(d.client));
  dealsCompleted.forEach((d) => completedClients.add(d.client));

  let potentialCount = 0;
  proposalClients.forEach((client) => {
    if (!completedClients.has(client)) potentialCount += 1;
  });

  const clientValues = {};

  dealsInProgress.forEach((d) => {
    clientValues[d.client] = (clientValues[d.client] || 0) + d.value;
  });

  dealsCompleted.forEach((d) => {
    clientValues[d.client] = (clientValues[d.client] || 0) + d.value;
  });

  const topClients = Object.entries(clientValues)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([client, value]) => ({ client, value }));

  return {
    totalClients: clients.length,
    activeClients: activeClients.size,
    potentialClients: potentialCount,
    topClients,
  };
}
